import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AppContext } from '../AppContext';
import { format12 } from '../scripts/SmartAzanClock'
import { ThemePalettes } from '../data/DisplayThemes';
import { FontAwesome } from '../data/FontAwesome';
import { CalculationMethods } from '../data/CalculationMethods';

const EGYPT_QURAN_RADIO_STREAM = 'https://stream.radiojar.com/8s5u5tpdtwzuv';
const DASHBOARD_DESIGN_WIDTH = 1680;
const DASHBOARD_DESIGN_HEIGHT = 900;

export default function Clock() {

    const { showMenu, setShowMenu, nextText, todaysDate, hijriDate, locationSettings,
        calculationSettings, deviceSettings, vakits, displayTime, currentVakit, nextVakit,
        background, time, midnightTime, twoThirdTime,
        language, isRtl, t, isAudioPlaying } = useContext(AppContext)
    const driftRef = useRef(null)
    const quranRadioRef = useRef(null)
    const quranRadioRetryTimerRef = useRef(null)
    const quranRadioRetryCountRef = useRef(0)
    const quranRadioShouldPlayRef = useRef(false)
    const quranRadioSuspendedForAudioRef = useRef(false)
    const [isQuranRadioPlaying, setIsQuranRadioPlaying] = useState(false)
    const [quranRadioStatus, setQuranRadioStatus] = useState('')
    const [dashboardScale, setDashboardScale] = useState(1)
    const [dashboardTimeFormat, setDashboardTimeFormat] = useState('12')
    const handleClick = () => {
        setShowMenu(!showMenu)
    }

    const handleDashboardKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowMenu(!showMenu);
        }
    }

    const clearQuranRadioRetry = () => {
        if (quranRadioRetryTimerRef.current) {
            clearTimeout(quranRadioRetryTimerRef.current);
            quranRadioRetryTimerRef.current = null;
        }
    }

    const scheduleQuranRadioReconnect = (minimumDelay = 3000) => {
        if (!quranRadioShouldPlayRef.current
            || quranRadioSuspendedForAudioRef.current
            || quranRadioRetryTimerRef.current) return;

        const retryDelay = Math.max(
            minimumDelay,
            Math.min(30000, 3000 * (2 ** quranRadioRetryCountRef.current))
        );
        quranRadioRetryCountRef.current += 1;
        setIsQuranRadioPlaying(true);
        setQuranRadioStatus(t('dashboard.radioConnecting'));
        quranRadioRetryTimerRef.current = setTimeout(() => {
            quranRadioRetryTimerRef.current = null;
            connectQuranRadio(true);
        }, retryDelay);
    }

    const connectQuranRadio = async (reloadStream = false) => {
        const audio = quranRadioRef.current;
        if (!audio
            || !quranRadioShouldPlayRef.current
            || quranRadioSuspendedForAudioRef.current) return;

        try {
            setIsQuranRadioPlaying(true);
            setQuranRadioStatus(t('dashboard.radioConnecting'));
            if (reloadStream || !audio.getAttribute('src')) {
                audio.src = EGYPT_QURAN_RADIO_STREAM;
                audio.load();
            }
            await audio.play();
        } catch (error) {
            scheduleQuranRadioReconnect();
        }
    }

    const stopQuranRadio = () => {
        const audio = quranRadioRef.current;
        quranRadioShouldPlayRef.current = false;
        quranRadioSuspendedForAudioRef.current = false;
        quranRadioRetryCountRef.current = 0;
        clearQuranRadioRetry();
        if (audio) {
            audio.pause();
            audio.removeAttribute('src');
            audio.load();
        }
        setIsQuranRadioPlaying(false);
        setQuranRadioStatus(t('dashboard.radioPaused'));
    }

    const toggleQuranRadio = (e) => {
        e.stopPropagation();
        if (quranRadioShouldPlayRef.current) {
            stopQuranRadio();
            return;
        }

        quranRadioShouldPlayRef.current = true;
        quranRadioRetryCountRef.current = 0;
        if (isAudioPlaying) {
            quranRadioSuspendedForAudioRef.current = true;
            setIsQuranRadioPlaying(true);
            setQuranRadioStatus(t('dashboard.radioPausedForAudio'));
            return;
        }
        connectQuranRadio(true);
    }

    useEffect(() => {
        const radio = quranRadioRef.current;

        if (isAudioPlaying) {
            if (quranRadioShouldPlayRef.current) {
                quranRadioSuspendedForAudioRef.current = true;
                clearQuranRadioRetry();
                radio?.pause();
                setIsQuranRadioPlaying(true);
                setQuranRadioStatus(t('dashboard.radioPausedForAudio'));
            }
            return;
        }

        if (quranRadioSuspendedForAudioRef.current) {
            quranRadioSuspendedForAudioRef.current = false;
            if (quranRadioShouldPlayRef.current) {
                const resumeTimer = setTimeout(() => connectQuranRadio(true), 500);
                return () => clearTimeout(resumeTimer);
            }
        }
        // The radio helpers use refs for the live playback state; rerun only when audio priority changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAudioPlaying])

    useEffect(() => () => {
        quranRadioShouldPlayRef.current = false;
        quranRadioSuspendedForAudioRef.current = false;
        clearQuranRadioRetry();
    }, [])

    const theme = ThemePalettes[deviceSettings.theme] || ThemePalettes.classic;

    useEffect(() => {
        const el = driftRef.current
        if (!el) return
        const isDashboard = deviceSettings.layout === 'dashboard'
        const applyDrift = (x, y) => {
            if (isDashboard) {
                el.style.setProperty('--screen-drift-x', `${x}px`)
                el.style.setProperty('--screen-drift-y', `${y}px`)
            } else {
                el.style.transform = `translate(${x}px, ${y}px)`
            }
        }
        if (deviceSettings.screenSaver !== 'Y') {
            applyDrift(0, 0)
            document.body.style.backgroundPosition = '50% 50%'
            return
        }
        const speed = 0.22 // gentle movement to reduce the risk of screen burn-in
        let x = 0, y = 0, dx = speed, dy = speed * 0.7
        const interval = setInterval(() => {
            const driftTarget = el
            if (!driftTarget) return
            const rect = driftTarget.getBoundingClientRect()
            const zoomed = deviceSettings.zoomedIn === 'Y'
            const diffX = Math.abs(window.innerWidth - rect.width) / 2
            const diffY = Math.abs(window.innerHeight - rect.height) / 2
            const maxX = isDashboard ? 10 : (zoomed ? 30 : Math.min(Math.max(diffX, 18), 42))
            const maxY = isDashboard ? 7 : (zoomed ? 20 : Math.min(Math.max(diffY, 14), 32))
            x += dx
            y += dy
            if (x >= maxX || x <= -maxX) dx = -dx
            if (y >= maxY || y <= -maxY) dy = -dy
            x = Math.max(-maxX, Math.min(maxX, x))
            y = Math.max(-maxY, Math.min(maxY, y))
            applyDrift(x, y)
            if (background.length > 0)
                document.body.style.backgroundPosition = `${50 + x * 0.15}% ${50 + y * 0.15}%`
        }, 50)
        return () => clearInterval(interval)
    }, [deviceSettings.screenSaver, deviceSettings.zoomedIn, deviceSettings.layout, background.length])

    useEffect(() => {
        const updateDashboardScale = () => {
            const viewport = window.visualViewport;
            const viewportWidth = viewport?.width || window.innerWidth;
            const viewportHeight = viewport?.height || window.innerHeight;
            const safeGutter = Math.max(20, Math.min(viewportWidth, viewportHeight) * 0.025);
            const widthScale = (viewportWidth - safeGutter * 2) / DASHBOARD_DESIGN_WIDTH;
            const heightScale = (viewportHeight - safeGutter * 2) / DASHBOARD_DESIGN_HEIGHT;
            setDashboardScale(Math.max(0.2, Math.min(widthScale, heightScale)));
        }

        updateDashboardScale();
        window.addEventListener('resize', updateDashboardScale);
        window.addEventListener('orientationchange', updateDashboardScale);
        window.visualViewport?.addEventListener('resize', updateDashboardScale);

        return () => {
            window.removeEventListener('resize', updateDashboardScale);
            window.removeEventListener('orientationchange', updateDashboardScale);
            window.visualViewport?.removeEventListener('resize', updateDashboardScale);
        }
    }, [])

    useEffect(() => {
        updateBackground(deviceSettings.layout === 'dashboard' ? '' : background);
        if (deviceSettings.layout !== 'dashboard') {
            document.title = `AzanClock • ${prayerName(currentVakit.name)} • ${t('clock.next')}: ${prayerName(nextVakit.name)} ${t('common.at')} ${nextVakit.time} • ${locationSettings.address}`;
        }
        // The translated title and visual background only need recalculation when these values change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceSettings.layout, background, currentVakit.name, nextVakit.name, nextVakit.time, locationSettings.address, language])

    const prayerName = (name) => t('prayers.' + name);

    const getClockDate = () => {
        if (language === 'pt-BR') {
            return normalizeDigits(new Date().toLocaleDateString('pt-BR', {
                timeZone: locationSettings.timeZoneID,
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            }));
        }

        if (!isRtl)
            return todaysDate;

        return normalizeDigits(new Date().toLocaleDateString('ar-EG', {
            timeZone: locationSettings.timeZoneID,
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }));
    }

    const getClockHijriDate = () => {
        if (language === 'pt-BR') {
            return normalizeDigits(new Date().toLocaleDateString('pt-BR-u-ca-islamic-umalqura', {
                timeZone: locationSettings.timeZoneID,
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            }));
        }

        if (!isRtl)
            return hijriDate;

        return normalizeDigits(new Date().toLocaleDateString('ar-SA-u-ca-islamic-umalqura', {
            timeZone: locationSettings.timeZoneID,
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).replace('هـ', '').trim() + ' هـ');
    }

    const getDashboardDate = () => {
        const now = new Date();
        const locale = language === 'ar' ? 'ar-EG' : language;
        const hijriLocale = language === 'ar' ? 'ar-SA-u-ca-islamic-umalqura' : `${language}-u-ca-islamic-umalqura`;

        return {
            weekday: normalizeDigits(now.toLocaleDateString(locale, {
                timeZone: locationSettings.timeZoneID,
                weekday: 'long'
            })),
            gregorianLong: normalizeDigits(now.toLocaleDateString(locale, {
                timeZone: locationSettings.timeZoneID,
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })),
            hijriLong: normalizeDigits(now.toLocaleDateString(hijriLocale, {
                timeZone: locationSettings.timeZoneID,
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }).replace('هـ', '').trim()),
            hijriMonthName: normalizeDigits(now.toLocaleDateString(hijriLocale, {
                timeZone: locationSettings.timeZoneID,
                month: 'long'
            }).replace('هـ', '').trim()),
            gregorian: normalizeDigits(now.toLocaleDateString(locale, {
                timeZone: locationSettings.timeZoneID,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            })),
            hijri: normalizeDigits(now.toLocaleDateString(hijriLocale, {
                timeZone: locationSettings.timeZoneID,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).replace('هـ', '').trim())
        };
    }

    const format24 = (value) => {
        const [hour = '00', minute = '00'] = String(value || '').split(':');
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }

    const normalizeDigits = (value) => String(value)
        .replace(/[٠-٩]/g, digit => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
        .replace(/[۰-۹]/g, digit => '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit));

    const getIslamicParts = (date) => {
        const parts = new Intl.DateTimeFormat('en-SA-u-ca-islamic-umalqura', {
            timeZone: locationSettings.timeZoneID,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        }).formatToParts(date);

        return {
            day: Number(parts.find(p => p.type === 'day')?.value),
            month: Number(parts.find(p => p.type === 'month')?.value),
            year: Number(parts.find(p => p.type === 'year')?.value)
        };
    }

    const daysUntilIslamicDate = (targetMonth, targetDay) => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        for (let days = 0; days <= 420; days++) {
            const candidate = new Date(start);
            candidate.setDate(start.getDate() + days);
            const parts = getIslamicParts(candidate);

            if (parts.month === targetMonth && parts.day === targetDay) {
                return days;
            }
        }

        return null;
    }

    const formatGregorianEventDate = (date) => {
        const locale = language === 'ar' ? 'ar-EG' : language;
        return normalizeDigits(date.toLocaleDateString(locale, {
            timeZone: locationSettings.timeZoneID,
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }));
    }

    const formatHijriEventDate = (year, month, day) => {
        const date = findGregorianForIslamicDate(year, month, day);
        if (!date) return '';
        const locale = language === 'ar' ? 'ar-SA-u-ca-islamic-umalqura' : `${language}-u-ca-islamic-umalqura`;
        return normalizeDigits(date.toLocaleDateString(locale, {
            timeZone: locationSettings.timeZoneID,
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).replace('هـ', '').trim());
    }

    const findGregorianForIslamicDate = (targetYear, targetMonth, targetDay) => {
        const today = new Date();
        const start = new Date(today.getFullYear() - 1, 0, 1);

        for (let days = 0; days <= 900; days++) {
            const candidate = new Date(start);
            candidate.setDate(start.getDate() + days);
            const parts = getIslamicParts(candidate);

            if (parts.year === targetYear && parts.month === targetMonth && parts.day === targetDay) {
                return candidate;
            }
        }

        return null;
    }

    const getIslamicEvents = () => {
        const currentHijriYear = getIslamicParts(new Date()).year;
        const definitions = [
            { key: 'startHijriYear', label: t('dashboard.startHijriYear'), month: 1, day: 1 },
            { key: 'ashura', label: t('dashboard.ashura'), month: 1, day: 10 },
            { key: 'mawlid', label: t('dashboard.mawlid'), month: 3, day: 12 },
            { key: 'startRamadan', label: t('dashboard.startRamadan'), month: 9, day: 1 },
            { key: 'eidFitr', label: t('dashboard.eidFitr'), month: 10, day: 1 },
            { key: 'eidAdha', label: t('dashboard.eidAdha'), month: 12, day: 10 },
            { key: 'nextHijriYear', label: t('dashboard.startHijriYear'), year: currentHijriYear + 1, month: 1, day: 1 }
        ];

        return {
            year: currentHijriYear,
            items: definitions.map(event => {
                const year = event.year || currentHijriYear;
                const date = findGregorianForIslamicDate(year, event.month, event.day);
                const today = new Date();
                const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

                return {
                    ...event,
                    year,
                    gregorian: date ? formatGregorianEventDate(date) : '-',
                    hijri: formatHijriEventDate(year, event.month, event.day),
                    isPast: date ? date < todayStart : false
                };
            })
        };
    }

    const dashboardCalendar = useMemo(() => ({
        dates: getDashboardDate(),
        islamicEvents: getIslamicEvents(),
        ramadanDays: daysUntilIslamicDate(9, 1),
        eidFitrDays: daysUntilIslamicDate(10, 1),
        eidAdhaDays: daysUntilIslamicDate(12, 10)
        // These helpers close over the language, timezone and date values listed below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [language, locationSettings.timeZoneID, todaysDate, hijriDate]);

    const renderDashboard = () => {
        const { dates, islamicEvents, ramadanDays, eidFitrDays, eidAdhaDays } = dashboardCalendar;
        const dailyPrayerNames = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
        const dailyPrayers = dailyPrayerNames
            .map(name => vakits.find(v => v.name === name))
            .filter(Boolean);
        const sunrise = vakits.find(v => v.name === 'Sunrise');
        const currentName = prayerName(currentVakit.name);
        const nextName = prayerName(nextVakit.name);
        const dashboardDisplayTime = dashboardTimeFormat === '24' ? format24(time) : displayTime;
        const calculationMethodName = CalculationMethods[calculationSettings.method]?.name || calculationSettings.method;
        const bottomCards = [
            { label: prayerName('Sunrise'), value: sunrise ? format12(sunrise.time) : '-' },
            { label: t('settings.midnight'), value: format12(midnightTime) },
            { label: t('dashboard.lastThird'), value: format12(twoThirdTime) },
            { label: t('dashboard.ramadan'), value: ramadanDays, suffix: t('dashboard.daysLeft') },
            { label: t('dashboard.eidFitr'), value: eidFitrDays, suffix: t('dashboard.daysLeft') },
            { label: t('dashboard.eidAdha'), value: eidAdhaDays, suffix: t('dashboard.daysLeft') }
        ];

        return (
            <div className={'dashboard-display ' + (isRtl ? 'dashboard-rtl' : '')}
                style={{
                    '--dashboard-bg': background ? `url(${background})` : 'none',
                    '--dashboard-scale': dashboardScale,
                    '--dashboard-frame-width': `${DASHBOARD_DESIGN_WIDTH * dashboardScale}px`,
                    '--dashboard-frame-height': `${DASHBOARD_DESIGN_HEIGHT * dashboardScale}px`
                }}
                onClick={handleClick}
                onKeyDown={handleDashboardKeyDown}
                role="button"
                tabIndex="0"
                autoFocus>
                <div className='dashboard-scroll-frame'>
                    <div className='dashboard-glass-stage' ref={driftRef}>
                    <aside className='dashboard-left-rail'>
                        <div className='dashboard-left-stack'>
                            <div className='dashboard-radio-card' onClick={(e) => e.stopPropagation()}>
                                <div className='dashboard-radio-copy'>
                                    <span className='dashboard-radio-icon'>{FontAwesome.Book}</span>
                                    <div>
                                        <strong>{t('dashboard.quranRadioTitle')}</strong>
                                        <small>{quranRadioStatus || t('dashboard.quranRadioSubtitle')}</small>
                                    </div>
                                </div>
                                <button className={'dashboard-radio-button ' + (isQuranRadioPlaying ? 'is-playing' : '')} onClick={toggleQuranRadio} type='button'>
                                    {isQuranRadioPlaying ? FontAwesome.Pause : FontAwesome.Play}
                                    <span>{isQuranRadioPlaying ? t('dashboard.radioStop') : t('dashboard.radioPlay')}</span>
                                </button>
                                <audio
                                    ref={quranRadioRef}
                                    src={EGYPT_QURAN_RADIO_STREAM}
                                    preload='none'
                                    onPlay={() => {
                                        clearQuranRadioRetry();
                                        quranRadioRetryCountRef.current = 0;
                                        setIsQuranRadioPlaying(true);
                                        setQuranRadioStatus(t('dashboard.radioLive'));
                                    }}
                                    onPlaying={() => {
                                        clearQuranRadioRetry();
                                        quranRadioRetryCountRef.current = 0;
                                        setIsQuranRadioPlaying(true);
                                        setQuranRadioStatus(t('dashboard.radioLive'));
                                    }}
                                    onPause={() => {
                                        if (!quranRadioShouldPlayRef.current) setIsQuranRadioPlaying(false);
                                    }}
                                    onWaiting={() => scheduleQuranRadioReconnect(5000)}
                                    onStalled={() => scheduleQuranRadioReconnect(5000)}
                                    onEnded={() => scheduleQuranRadioReconnect()}
                                    onError={() => {
                                        if (quranRadioShouldPlayRef.current) {
                                            scheduleQuranRadioReconnect();
                                        } else {
                                            setIsQuranRadioPlaying(false);
                                        }
                                    }}
                                />
                            </div>

                            <a className='dashboard-reset-card' href='/reset/' onClick={(e) => e.stopPropagation()}>
                                <span>{FontAwesome.Reset}</span>
                                <strong>{t('common.reset')}</strong>
                            </a>

                            <div className='dashboard-bottom-cards'>
                                {bottomCards.map(card => (
                                    <div className={'dashboard-info-card ' + (card.highlight ? 'is-highlight' : '')} key={card.label}>
                                        <span>{card.label}</span>
                                        <strong>{card.value ?? '-'}</strong>
                                        {card.suffix ? <small>{card.suffix}</small> : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>

                    <main className='dashboard-center-stack'>
                        <div className='dashboard-date-glass'>
                            <div className='dashboard-date-head'>
                                <div className='dashboard-day-name'>{dates.weekday}</div>
                                <div className='dashboard-calligraphy-word'>{dates.hijriMonthName}</div>
                            </div>
                            <div className='dashboard-date-pair'>
                                <span>{dates.gregorianLong} <b>{dates.gregorian}</b></span>
                                <span>{dates.hijriLong} {isRtl ? 'هـ' : ''} <b>{dates.hijri}</b></span>
                            </div>
                            <div className='dashboard-site-strip'>
                                <span className='dashboard-site-icon'>{FontAwesome.Book}</span>
                                <span>{locationSettings.address}</span>
                                <strong>{calculationMethodName}</strong>
                            </div>
                        </div>

                        <div className='dashboard-hero-card dashboard-main-glass'>
                            <div className='dashboard-hero-split'>
                                <div className='dashboard-time-side'>
                                    <div className='dashboard-side-label'>{t('dashboard.currentTime')}</div>
                                    <div className='dashboard-live-clock'>{dashboardDisplayTime}</div>
                                    <button
                                        className='dashboard-time-toggle'
                                        type='button'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDashboardTimeFormat(dashboardTimeFormat === '12' ? '24' : '12');
                                        }}>
                                        {dashboardTimeFormat === '12' ? '24H' : '12H'}
                                    </button>
                                </div>

                                <div className='dashboard-prayer-side'>
                                    <div className='dashboard-current-prayer-label'>{t('dashboard.currentPrayer')}</div>
                                    <div className='dashboard-hero-prayer'>{currentName}</div>
                                    <div className='dashboard-hero-meta'>
                                        <span>{t('dashboard.remainingTo')} {nextName}</span>
                                        <strong>{nextText}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='dashboard-prayers-dock dashboard-prayer-glass'>
                            <div className='dashboard-timeline-head'>
                                <span>{t('dashboard.todayPrayers')}</span>
                                <span>{t('dashboard.remainingTo')} {nextName} {nextText}</span>
                            </div>
                            <div className='dashboard-prayer-track'>
                                {dailyPrayers.map(v => (
                                    <div className={'dashboard-track-item ' + (v.name === currentVakit.name ? 'is-current' : '')} key={v.name}>
                                        <div className='dashboard-track-dot'></div>
                                        <div className='dashboard-track-name'>{prayerName(v.name)}</div>
                                        <div className='dashboard-track-time'>{format12(v.time)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </main>

                    <aside className='dashboard-events-panel'>
                        <div className='dashboard-events-title'>
                            <span>{t('dashboard.islamicEvents')}</span>
                            <strong>{t('dashboard.eventsForYear')} {islamicEvents.year} {isRtl ? 'هـ' : 'AH'}</strong>
                        </div>

                        <div className='dashboard-events-line'>
                            {islamicEvents.items.map(event => (
                                <div className={'dashboard-event-row ' + (event.isPast ? 'is-past' : '')} key={event.key + event.year}>
                                    <div className='dashboard-event-marker'></div>
                                    <div>
                                        <strong>{event.label}</strong>
                                        <span>{event.hijri} {isRtl ? 'هـ' : ''}</span>
                                        <small>{event.gregorian}</small>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className='dashboard-event-note'>{t('dashboard.eventNote')}</p>
                    </aside>

                    </div>
                </div>
            </div>
        );
    }


    const updateBackground = (bg) => {
        document.body.style.setProperty('--clock-bg-brightness', theme.backgroundBrightness);
        document.body.style.setProperty('--clock-vignette-opacity', theme.vignette);
        if (bg.length > 0) {
            document.body.style.backgroundImage = 'url(' + bg + ')';
            document.body.style.backgroundSize = theme.backgroundSize;
            document.body.style.backgroundRepeat = 'no-repeat';
        }
        else {
            document.body.style.backgroundImage = null;
            document.body.style.backgroundSize = null;
            document.body.style.backgroundRepeat = null;
            document.body.style.backgroundPosition = null;
        }
    }

    const renderPrayerOrbit = () => {
        const orbitPrayerNames = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
        const orbitPrayers = orbitPrayerNames
            .map(name => vakits.find(v => v.name === name))
            .filter(Boolean);
        const currentName = prayerName(currentVakit.name);
        const nextName = prayerName(nextVakit.name);
        const calculationMethodName = CalculationMethods[calculationSettings.method]?.name || calculationSettings.method;

        return (
            <div
                className={'prayer-orbit-display ' + (isRtl ? 'prayer-orbit-rtl ' : '') + (deviceSettings.zoomedIn === 'Y' ? 'is-zoomed' : '')}
                onClick={handleClick}
                onKeyDown={handleDashboardKeyDown}
                role='button'
                tabIndex='0'
                autoFocus>
                <div className='prayer-orbit-drift' ref={driftRef}>
                    <header className='prayer-orbit-header'>
                        <div className='prayer-orbit-date'>
                            <span>{getClockDate()}</span>
                            <strong>{getClockHijriDate()}</strong>
                        </div>
                        <div className='prayer-orbit-location'>
                            <span>{locationSettings.address}</span>
                            <small>{calculationMethodName}</small>
                        </div>
                    </header>

                    <main className='prayer-orbit-stage'>
                        <div className='prayer-orbit-ring' aria-label={t('dashboard.todayPrayers')}>
                            <div className='prayer-orbit-track' aria-hidden='true'></div>
                            {orbitPrayers.map((v, index) => {
                                const angle = -90 + (index * 360 / orbitPrayers.length);
                                return (
                                    <div
                                        className={'prayer-orbit-item '
                                            + (v.name === currentVakit.name ? 'is-current ' : '')
                                            + (v.name === nextVakit.name ? 'is-next' : '')}
                                        style={{
                                            '--orbit-angle': `${angle}deg`,
                                            '--orbit-counter-angle': `${-angle}deg`
                                        }}
                                        key={v.name}>
                                        <span className='prayer-orbit-dot'></span>
                                        <strong>{prayerName(v.name)}</strong>
                                        <time>{format12(v.time)}</time>
                                    </div>
                                );
                            })}

                            <div className='prayer-orbit-center'>
                                <span className='prayer-orbit-eyebrow'>{t('dashboard.currentTime')}</span>
                                <time className='prayer-orbit-time'>{displayTime}</time>
                                <div className='prayer-orbit-current'>
                                    <small>{t('dashboard.currentPrayer')}</small>
                                    <strong>{currentName}</strong>
                                </div>
                                <div className='prayer-orbit-next'>
                                    <span>{t('dashboard.remainingTo')} {nextName}</span>
                                    <b>{nextText}</b>
                                </div>
                            </div>
                        </div>
                    </main>

                    <footer className='prayer-orbit-footer'>
                        <span>{t('dashboard.todayPrayers')}</span>
                        <strong>{t('clock.next')}: {nextName} · {format12(nextVakit.time)}</strong>
                    </footer>
                </div>
            </div>
        );
    }

    if (deviceSettings.layout === 'dashboard') {
        return renderDashboard();
    }

    return renderPrayerOrbit();
}
